/**
 * FuelPriceTicker - Header widget showing current fuel spot price and reserves
 * Click to expand FuelPanel
 */

import React, { useMemo, useState, useEffect } from "react";
import { getPriceTrend, formatGallons } from "../../systems/FuelSystem.js";

export function FuelPriceTicker({ fuelState, onClick, chaos }) {
  const baseSpotPrice = fuelState?.spotPrice || 2.95;
  const { change, trend } = useMemo(() => getPriceTrend(fuelState || {}), [fuelState]);

  // CHAOS MODE: Show wildly fluctuating prices
  const [chaosPrice, setChaosPrice] = useState(null);
  useEffect(() => {
    if (chaos?.active) {
      // Initial random price
      const range = chaos.priceMax - chaos.priceMin;
      setChaosPrice(chaos.priceMin + (Math.random() * range));
      // Fluctuate price every 500ms for visual effect
      const interval = setInterval(() => {
        setChaosPrice(chaos.priceMin + (Math.random() * range));
      }, 500);
      return () => clearInterval(interval);
    } else {
      setChaosPrice(null);
    }
  }, [chaos?.active, chaos?.priceMin, chaos?.priceMax]);

  const spotPrice = chaosPrice !== null ? chaosPrice : baseSpotPrice;
  const isChaos = chaos?.active;

  // Calculate total fuel reserves (active hedges + unused contract allocations)
  const reserves = useMemo(() => {
    let total = 0;
    // Sum hedge gallons remaining
    (fuelState?.hedges || []).forEach(h => {
      if (h.status === 'active' && h.gallonsRemaining > 0) {
        total += h.gallonsRemaining;
      }
    });
    // Sum contract allocations available this month
    (fuelState?.contracts || []).forEach(c => {
      if (c.status === 'active') {
        const available = (c.monthlyAllocationGal || 0) - (c.usedThisMonth || 0);
        if (available > 0) total += available;
      }
    });
    return total;
  }, [fuelState?.hedges, fuelState?.contracts]);

  // Color based on trend (chaos overrides)
  const trendColor = isChaos ? '#ec4899' : trend === 'up' ? '#ef4444' : trend === 'down' ? '#22c55e' : '#737373';
  const trendIcon = isChaos ? '⚡' : trend === 'up' ? '↑' : trend === 'down' ? '↓' : '−';

  return (
    <button
      onClick={onClick}
      className="flex items-center gap-2 px-3 py-1.5 rounded transition-all hover:bg-gray-800"
      style={{
        backgroundColor: isChaos ? '#1a0a1a' : "#111",
        border: isChaos ? "1px solid #ec4899" : "1px solid #1a1a1a",
        animation: isChaos ? 'chaosGlow 0.5s ease-in-out infinite alternate' : 'none',
      }}
      title={isChaos ? "MARKET CHAOS - Prices fluctuating wildly!" : "Click to manage fuel contracts"}
    >
      <span className="text-xs" style={{ color: isChaos ? '#ec4899' : "#525252" }}>
        {isChaos ? 'CHAOS' : 'JET-A'}
      </span>
      <span
        className="font-mono font-bold text-sm"
        style={{ color: isChaos ? '#ec4899' : "#60ffd0" }}
      >
        ${spotPrice.toFixed(2)}
      </span>
      <span
        className="text-xs font-bold"
        style={{ color: trendColor }}
      >
        {trendIcon}{isChaos ? '' : Math.abs(change).toFixed(2)}
      </span>
      {reserves > 0 && !isChaos && (
        <>
          <span className="text-xs" style={{ color: "#333" }}>|</span>
          <span className="text-xs" style={{ color: "#737373" }}>
            {formatGallons(reserves)} gal
          </span>
        </>
      )}
      {isChaos && (
        <style>{`
          @keyframes chaosGlow {
            0% { box-shadow: 0 0 5px rgba(236, 72, 153, 0.3); }
            100% { box-shadow: 0 0 15px rgba(236, 72, 153, 0.6); }
          }
        `}</style>
      )}
    </button>
  );
}

// Compact version for tighter spaces
export function FuelPriceTickerCompact({ fuelState, onClick }) {
  const spotPrice = fuelState?.spotPrice || 2.95;
  const { trend } = useMemo(() => getPriceTrend(fuelState || {}), [fuelState]);

  const trendColor = trend === 'up' ? '#ef4444' : trend === 'down' ? '#22c55e' : '#737373';
  const trendIcon = trend === 'up' ? '↑' : trend === 'down' ? '↓' : '';

  return (
    <button
      onClick={onClick}
      className="flex items-center gap-1 px-2 py-1 rounded text-xs transition-all hover:bg-gray-800"
      style={{
        backgroundColor: "transparent",
        border: "1px solid #333",
      }}
      title="Fuel spot price"
    >
      <span style={{ color: "#525252" }}>⛽</span>
      <span className="font-mono" style={{ color: "#60ffd0" }}>
        ${spotPrice.toFixed(2)}
      </span>
      {trendIcon && (
        <span style={{ color: trendColor }}>{trendIcon}</span>
      )}
    </button>
  );
}

export default FuelPriceTicker;
